'use client';

import { CopyIcon, ThumbsUpIcon, ThumbsDownIcon } from 'lucide-react';
import type { UIMessage } from 'ai';
import type { CSSProperties } from 'react';
import { memo, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { TooltipProvider } from '@/shared/ui/shadcn/tooltip';

import { renderMessagePartsWithOptions, isTextPart, type ToolPartRenderer } from '../../model';
import { useCopilotToolUiOptional } from '../ToolUi/useCopilotToolUi';
import {
  Conversation,
  type ConversationContext,
  ConversationContent,
} from '../Conversation/Conversation';
import {
  Message,
  MessageContent,
  MessageActions,
  MessageAction,
  MessageToolbar,
} from '../Message/Message';
import { Shimmer } from '../Shimmer/Shimmer';
import { useLastAssistantMinHeight } from './useLastAssistantMinHeight';

type CopilotChatStatus = 'ready' | 'submitted' | 'streaming' | 'error';

export interface CopilotThreadProps {
  /** UIMessage stream from AI SDK. */
  messages: UIMessage[];
  /** AI SDK status (used for optimistic assistant placeholder behavior) */
  status?: CopilotChatStatus;
  toolPartRenderers?: Readonly<Record<string, ToolPartRenderer>>;
  /** Callback when copy action is clicked */
  onCopy?: (messageId: string, content: string) => void;
  /** Callback when thumbs up is clicked */
  onThumbsUp?: (messageId: string) => void;
  /** Callback when thumbs down is clicked */
  onThumbsDown?: (messageId: string) => void;
  /** Additional className for the container */
  className?: string;
}

interface CopilotMessageRowProps {
  messageId: string;
  role: UIMessage['role'];
  parts: UIMessage['parts'];
  isLastAssistantMessage: boolean;
  isBusy: boolean;
  toolPartRenderers?: Readonly<Record<string, ToolPartRenderer>>;
  containerRef?: (node: HTMLDivElement | null) => void;
  containerStyle?: CSSProperties;
  onCopyMessageText: (messageId: string, parts: UIMessage['parts']) => void;
  onThumbsUp: (messageId: string) => void;
  onThumbsDown: (messageId: string) => void;
}

const CopilotMessageRow = memo(function CopilotMessageRow({
  messageId,
  role,
  parts,
  isLastAssistantMessage,
  isBusy,
  toolPartRenderers,
  containerRef,
  containerStyle,
  onCopyMessageText,
  onThumbsUp,
  onThumbsDown,
}: CopilotMessageRowProps) {
  return (
    <div ref={containerRef} style={containerStyle}>
      <Message from={role}>
        <MessageContent>
          {renderMessagePartsWithOptions(parts, messageId, { toolPartRenderers })}
        </MessageContent>

        {role === 'assistant' && isLastAssistantMessage && !isBusy && (
          <MessageToolbar>
            <MessageActions>
              <MessageAction tooltip="Copy" onClick={() => onCopyMessageText(messageId, parts)}>
                <CopyIcon className="size-4" />
              </MessageAction>
              <MessageAction tooltip="Good response" onClick={() => onThumbsUp(messageId)}>
                <ThumbsUpIcon className="size-4" />
              </MessageAction>
              <MessageAction tooltip="Bad response" onClick={() => onThumbsDown(messageId)}>
                <ThumbsDownIcon className="size-4" />
              </MessageAction>
            </MessageActions>
          </MessageToolbar>
        )}
      </Message>
    </div>
  );
});

/**
 * CopilotThread — renders a complete AI conversation with messages,
 * reasoning blocks, tool calls, and follow-up suggestions.
 *
 * This is the main conversational UI component for the copilot feature.
 * It handles all the complexity of rendering different message types
 * internally, exposing only high-level callbacks.
 *
 * @example
 * ```tsx
 * <CopilotThread
 *   messages={messages}
 *   onCopy={(id, text) => console.log('Copied:', text)}
 * />
 * ```
 */
export function CopilotThread({
  messages,
  status = 'ready',
  toolPartRenderers,
  onCopy,
  onThumbsUp,
  onThumbsDown,
  className,
}: CopilotThreadProps) {
  const isBusy = status === 'submitted' || status === 'streaming';
  const toolUi = useCopilotToolUiOptional();
  const resolvedToolPartRenderers = toolPartRenderers ?? toolUi?.toolPartRenderers;
  const handleCopy = useCallback(
    (messageId: string, content: string) => {
      onCopy?.(messageId, content);
      // Default behavior: copy to clipboard
      if (!onCopy) {
        navigator.clipboard.writeText(content).catch(console.error);
      }
    },
    [onCopy]
  );

  const handleThumbsUp = useCallback(
    (messageId: string) => {
      onThumbsUp?.(messageId);
      if (!onThumbsUp) {
        console.log('[CopilotThread] Thumbs up:', messageId);
      }
    },
    [onThumbsUp]
  );

  const handleThumbsDown = useCallback(
    (messageId: string) => {
      onThumbsDown?.(messageId);
      if (!onThumbsDown) {
        console.log('[CopilotThread] Thumbs down:', messageId);
      }
    },
    [onThumbsDown]
  );

  /** Extract all text content from message parts */
  const getMessageText = useCallback(
    (parts: UIMessage['parts']): string =>
      parts
        .filter(isTextPart)
        .map((part) => part.text)
        .join(''),
    []
  );

  const lastAssistantMessageId = useMemo(
    () => [...messages].reverse().find((m) => m.role === 'assistant')?.id,
    [messages]
  );

  const lastRealMessage = messages.length > 0 ? messages[messages.length - 1] : undefined;
  const shouldShowOptimisticAssistant =
    (status === 'submitted' || status === 'streaming') && lastRealMessage?.role === 'user';

  const lastUserMessageIdToMeasure = useMemo(() => {
    if (shouldShowOptimisticAssistant) {
      return lastRealMessage?.role === 'user' ? lastRealMessage.id : undefined;
    }

    if (!lastAssistantMessageId) {
      return undefined;
    }

    let lastAssistantIndex = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m?.role === 'assistant' && m.id === lastAssistantMessageId) {
        lastAssistantIndex = i;
        break;
      }
    }
    if (lastAssistantIndex > 0) {
      for (let i = lastAssistantIndex - 1; i >= 0; i--) {
        const m = messages[i];
        if (m?.role === 'user') return m.id;
      }
    }

    return [...messages].reverse().find((m) => m.role === 'user')?.id;
  }, [lastAssistantMessageId, lastRealMessage, messages, shouldShowOptimisticAssistant]);

  const conversationContextRef = useRef<ConversationContext | null>(null);
  const lastScrolledUserIdRef = useRef<string | null>(null);
  const [scrollEl, setScrollEl] = useState<HTMLElement | null>(null);
  const [contentEl, setContentEl] = useState<HTMLElement | null>(null);

  useLayoutEffect(() => {
    const nextScrollEl = conversationContextRef.current?.scrollRef.current ?? null;
    const nextContentEl = conversationContextRef.current?.contentRef.current ?? null;

    // Synchronize DOM refs to state - legitimate use in layout effect
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (nextScrollEl !== scrollEl) setScrollEl(nextScrollEl);

    if (nextContentEl !== contentEl) setContentEl(nextContentEl);
  }, [contentEl, messages.length, scrollEl]);

  useLayoutEffect(() => {
    if (status !== 'submitted') return;
    if (!lastRealMessage || lastRealMessage.role !== 'user') return;

    if (lastScrolledUserIdRef.current === lastRealMessage.id) return;
    lastScrolledUserIdRef.current = lastRealMessage.id;

    const ctx = conversationContextRef.current;
    if (!ctx) return;

    const didScroll = ctx.scrollToBottom({ animation: 'instant', ignoreEscapes: true });
    void Promise.resolve(didScroll).then(() => {
      // Prevent subsequent resizes (e.g. streaming) from "following" the bottom automatically.
      ctx.stopScroll();
    });
  }, [lastRealMessage, status]);

  const [lastUserEl, setLastUserEl] = useState<HTMLElement | null>(null);
  const lastUserRef = useCallback((node: HTMLDivElement | null) => {
    setLastUserEl(node);
  }, []);

  const { minHeightPx } = useLastAssistantMinHeight({
    scrollEl,
    contentEl,
    lastUserEl,
  });

  const lastAssistantStyle = useMemo<CSSProperties | undefined>(() => {
    return minHeightPx ? { minHeight: `${minHeightPx}px` } : undefined;
  }, [minHeightPx]);

  const handleCopyMessageText = useCallback(
    (messageId: string, parts: UIMessage['parts']) => {
      handleCopy(messageId, getMessageText(parts));
    },
    [getMessageText, handleCopy]
  );

  return (
    <TooltipProvider>
      <Conversation className={className} contextRef={conversationContextRef}>
        <ConversationContent
          style={{
            paddingTop: '16px',
            paddingBottom: 'var(--dock-height, 96px)',
          }}
        >
          {messages.map((message) => (
            <CopilotMessageRow
              messageId={message.id}
              role={message.role}
              parts={message.parts}
              isLastAssistantMessage={
                !shouldShowOptimisticAssistant &&
                message.role === 'assistant' &&
                message.id === lastAssistantMessageId
              }
              isBusy={isBusy}
              toolPartRenderers={resolvedToolPartRenderers}
              key={message.id}
              containerRef={
                message.role === 'user' && message.id === lastUserMessageIdToMeasure
                  ? lastUserRef
                  : undefined
              }
              containerStyle={
                !shouldShowOptimisticAssistant &&
                message.role === 'assistant' &&
                message.id === lastAssistantMessageId
                  ? lastAssistantStyle
                  : undefined
              }
              onCopyMessageText={handleCopyMessageText}
              onThumbsDown={handleThumbsDown}
              onThumbsUp={handleThumbsUp}
            />
          ))}

          {shouldShowOptimisticAssistant && (
            <div style={lastAssistantStyle}>
              <Message from="assistant">
                <MessageContent>
                  <Shimmer className="text-xs font-medium">Thinking</Shimmer>
                </MessageContent>
              </Message>
            </div>
          )}
        </ConversationContent>
      </Conversation>
    </TooltipProvider>
  );
}
